import React, { useState, useContext } from 'react'
import {
  Divider,
  Grid,
  Radio,
  FormControlLabel,
  RadioGroup,
} from '@material-ui/core'
import {
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
} from '@stripe/react-stripe-js'
import { useLocation, useHistory } from 'react-router-dom'
import Cards from 'react-credit-cards'
import 'react-credit-cards/es/styles-compiled.css'
import FlexView from 'react-flexview/lib'
import {
  EventEmitterService,
  EventKey,
} from '../../../services/EventEmitterService'
import { SessionService } from '../../../services/SessionService'
import DataLayerService from '../../../services/DataLayerService'
import { awaitWithLoading, isInFlow } from '../../../services/utils'
import { UserApi } from '../../../api/user.api'
import { BrowserView, isBrowser, MobileView } from 'react-device-detect'
import { BackButton } from '../../Core/Buttons/BackButton'
import { GreenButton } from '../../Core/Buttons/GreenButton'
import { CreditCardLogo } from '../../Core/CreditCardLogo'
import { BottomSheetPolicySummary } from '../../Insurance/BottomSheetPolicySummary'
import NewFillButton from '../../Core/NewFillButton'
import { SessionContextStore } from '../../../context/SessionContext'
import {
  InsurancePurchaseContextStore,
  INSURANCE_CONTEXT_ACTIONS,
} from '../../../context/InsuracePurchaseContext'
import { BuyingInsuranceContextStore } from '../../../context/BuyingInsuranceContext'
import { PaymentMethod } from '../../../models/user-models/CreditCardModal'
import { Action } from '../../../models/Action'
import { TermsAndConditionsSection } from '../../Insurance/SummarySection/TermsAndConditionsSection'
import { Frame } from '../../Core/Frames/Frame'

var classNames = require('classnames')
const ccstyle = { base: { '::placeholder': { color: 'rgb(159, 173, 197)' } } }

const CreditCard = ({ onPurchase }) => {
  const offersContext = useContext(BuyingInsuranceContextStore)
  const stripe = useStripe()
  const elements = useElements()
  const sessionContext = useContext(SessionContextStore)
  const purchaseContext = useContext(InsurancePurchaseContextStore)
  const location = useLocation()
  const history = useHistory()
  const [installments, setInstallments] = React.useState('full')
  const [issuer, setIssuer] = useState('')
  const [expire, setExpire] = useState('0000')
  const [last4, setLast4] = useState('')
  const [focusedCC, setFocusedCC] = useState('number')
  const [error, setError] = useState('')

  const [editMode, setEditMode] = useState(!sessionContext.user.hasCreditCard)
  const [needToRenew, setneedToRenew] = useState(false)

  React.useEffect(() => {
    setEditMode(!sessionContext.user.hasCreditCard)
  }, [sessionContext.user])

  React.useEffect(() => {
    const fetchData = async () => {
      let res = await UserApi.hasFailedActivationInLast5Days()
      if (res.ok) {
        setneedToRenew(res.data.has_failed_activation_in_last5_days)
      }
    }
    fetchData()
  }, [sessionContext.user])

  const onCreditCardNumberChange = (e) => {
    setIssuer(e.brand)
    if (e.complete) {
      const element = elements.getElement(CardExpiryElement)
      element.focus()
      setFocusedCC('expiry')
    }
  }

  const onExpiryChange = (e) => {
    if (e.complete) {
      const element = elements.getElement(CardCvcElement)
      element.focus()
      setFocusedCC('cvc')
    }
  }

  const onContinue = () => {
    if (isInFlow(location.pathname))
      EventEmitterService.dispatch(EventKey.FLOW_CC_FINISH)
  }

  const onSave = async () => {
    if (!stripe || !elements) {
      return
    }

    setFocusedCC('number')

    const number = elements.getElement(CardNumberElement)

    EventEmitterService.dispatch(EventKey.ShowLoader)
    let res = await stripe.createToken(number)

    if (res.error) {
      EventEmitterService.dispatch(EventKey.DismissLoader)
      setError(res.error.message)
      return false
    } else if (res.token) {
      let ccServerRes = await UserApi.updateCreditCard(res.token)

      EventEmitterService.dispatch(EventKey.DismissLoader)

      if (ccServerRes.ok) {
        updateCreditCardVisual(
          res.token.card.exp_month,
          res.token.card.exp_year,
          res.token.card.last4,
          res.token.card.brand
        )
        DataLayerService.pushToDataLayer({
          event: 'successful-payment-saved',
          'payment-method': 'cc',
          price: offersContext.pricebreakdown?.price?.toFixed(2),
          period: offersContext.offerCart?.period?.plan,
        })
        return true
      } else {
        EventEmitterService.dispatch(EventKey.ShowError, ccServerRes)
        return false
      }
    } else {
      EventEmitterService.dispatch(EventKey.DismissLoader)
      setError('Unknown error from credit card service provider')
      return false
    }
  }

  const setEmptyCardVisual = () => {
    setExpire('')
    setLast4('')
    setIssuer('')
  }

  const updateCreditCardVisual = (month, year, last4, brand) => {
    setExpire(`${month < 10 ? '0' : ''}${month}/${year}`)
    setLast4('************' + last4)
    setIssuer(brand)
  }

  React.useEffect(() => {
    UserApi.getCreditCardInfo().then((res) => {
      if (res.ok) {
        updateCreditCardVisual(
          res.parsedData.expMonth,
          res.parsedData.expYear,
          res.parsedData.lastFourDigits,
          res.parsedData.brand
        )
      } else {
        setEmptyCardVisual()
      }
    })
  }, [])

  const editCreditCard = () => {
    setEditMode(true)
  }

  const onEditCreditCard = async () => {
    if (editMode) {
      let val = await awaitWithLoading(onSave())
      if (val) {
        setEditMode(false)
        onPurchase()
        // isInFlow(location.pathname) && onContinue()
        // EventEmitterService.dispatch(EventKey.ShowSuccess, { title: 'Credit Card Added', body: 'Credit Card saved successfully'})
      }
    } else {
      onPurchase()
    }
  }

  const onEditCreditCardAndPurache = async () => {
    const val = await awaitWithLoading(onSave())
    if (val) {
      setEditMode(false)
      onPurchase()
      const res = await awaitWithLoading(UserApi.purchaseAfterCcFailure())
      if (res.ok) {
        history.push('/insurance/congratulations')
      } else {
        EventEmitterService.dispatch(EventKey.ShowUserError, {
          title: 'Please Note',
          message: 'Your card was declined.',
        })
      }
    }
  }

  const paymentMethodChange = (event) => {
    setEditMode(event.target.value != PaymentMethod.BANK_TRANSFER)
    purchaseContext.dispatch(
      new Action(
        INSURANCE_CONTEXT_ACTIONS.SET_PAYMENT_METHOD,
        event.target.value
      )
    )
  }

  return (
    <React.Fragment>
      {SessionService.isAdmin() && isInFlow(location.pathname) && (
        <RadioGroup
          onChange={paymentMethodChange}
          value={purchaseContext.paymentMethod}>
          <FlexView>
            <FormControlLabel
              value={PaymentMethod.STRIPE}
              label={'Credit Card'}
              control={<Radio color="primary" />}
            />
            <FormControlLabel
              value={PaymentMethod.BANK_TRANSFER}
              label={'Generate policy without Credit Card'}
              control={<Radio color="primary" />}
            />
          </FlexView>
        </RadioGroup>
      )}
      {/* <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                <div className='clickable' onClick={() => setInstallments('full')}>
                    <SkyPaper isHighlighted={installments == 'full'}>
                        <FlexView vAlignContent={'center'}>
                            <img className='x-large-icon' src={`${process.env.PUBLIC_URL}/assets/cc_full.jpg`} />
                            <div className='margin-left-basic'>
                                <div className='blue bold text-title'>PAY BASE RATE IN FULL</div>
                                <BlueGrayBox style={{padding: 16, borderRadius: 10, backgroundColor: '#e2e8ff'}} className='margin-top-basic'>
                                    <div className='text-small blue'>Pay <span className='bold'>$300</span> today +monthly per</div>
                                    <div className='text-small margin-top-small blue'>mile charge (estimated 50$ a month)</div>
                                </BlueGrayBox>
                            </div>
                        </FlexView>
                    </SkyPaper>
                </div>
                </Grid>
                <Grid item xs={12} md={6}>
                    <div className='clickable' onClick={() => setInstallments('installments')}>
                        <SkyPaper isHighlighted={installments == 'installments'}>
                            <FlexView vAlignContent={'center'}>
                                <img className='x-large-icon' src={`${process.env.PUBLIC_URL}/assets/cc_installments.jpg`} />
                                <div className='margin-left-basic'>
                                    <div className='blue bold text-title'>PAY BY INSTALLMENTS</div>
                                    <BlueGrayBox style={{padding: 16, borderRadius: 10, backgroundColor: '#e2e8ff'}} className='margin-top-basic'>
                                        <div className='text-small blue'>Pay <span className='bold'>$100</span> today +11 monthly</div>
                                        <div className='text-small margin-top-small blue'>payments (estimated 60$ a month)</div>
                                    </BlueGrayBox>
                                </div>
                            </FlexView>
                        </SkyPaper>
                    </div>
                </Grid>
            </Grid> */}
      {isInFlow(location.pathname) && (
        <Frame>
          <div>
            If you would like to purchase coverage now please press the "Begin
            Coverage" button to begin your transaction. It is recommended you
            view the entire sample policy before purchasing to understand what
            you are buying.
            <br />
            By clicking "Begin Coverage" you agree to the terms and conditions
            shown below
          </div>
        </Frame>
      )}
      <Grid className="margin-top-basic" container spacing={4}>
        <Grid item xs sm md={6}>
          {purchaseContext.paymentMethod == PaymentMethod.STRIPE && (
            <>
              {editMode && (
                <React.Fragment>
                  <CardNumberElement
                    onFocus={() => setFocusedCC('number')}
                    onReady={(e) => e.focus()}
                    options={{
                      placeholder: 'xxxx-xxxx-xxxx-xxxx',
                      style: ccstyle,
                      classes: { base: 'test' },
                    }}
                    onChange={onCreditCardNumberChange}
                  />
                  <div className="black text-body margin-top-medium">
                    Expiration date
                  </div>
                  <FlexView className="margin-top-basic">
                    <div className="full-width">
                      <CardExpiryElement
                        onFocus={() => setFocusedCC('expiry')}
                        onChange={onExpiryChange}
                        options={{
                          classes: { base: 'test' },
                          style: ccstyle,
                        }}
                      />
                    </div>
                    <div className="cvc margin-left-basic full-width">
                      <CardCvcElement
                        onFocus={() => setFocusedCC('cvc')}
                        options={{
                          placeholder: 'cvc',
                          classes: {
                            base: 'cvc-container',
                          },
                          style: ccstyle,
                        }}
                      />
                    </div>
                  </FlexView>
                </React.Fragment>
              )}

              {!editMode && (
                <React.Fragment>
                  <FlexView vAlignContent={'center'}>
                    <CreditCardLogo brand={issuer} />
                    <div className="text-small black margin-left-6">
                      xxxx-xxxx-xxxx-
                      {last4.split('*')[last4.split('*').length - 1]}
                    </div>
                    <div
                      onClick={editCreditCard}
                      className="text-small blue clickable underline margin-left-6">
                      Edit card
                    </div>
                  </FlexView>
                </React.Fragment>
              )}
            </>
          )}
          <div className="margin-top-basic">
            <BrowserView>
              {!isInFlow(location.pathname) &&
                editMode &&
                (needToRenew ? (
                  <>
                    <Grid item xs={6}>
                      <GreenButton
                        disabled={!editMode}
                        onClick={onEditCreditCardAndPurache}>
                        Complete purchase
                      </GreenButton>
                    </Grid>
                    <Grid className="margin-top-basic margin-bottom-medium">
                      your credit card charge has failed and as a result, your
                      renewal policy was not created. To finalize your purchase,
                      please update your credit card information and click on
                      the "complete purchase" button.
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={6}>
                      <NewFillButton
                        disabled={!editMode}
                        onClick={onEditCreditCard}>
                        Save Changes
                      </NewFillButton>
                    </Grid>
                  </>
                ))}
            </BrowserView>
          </div>
          {error && (
            <div className="text-body red margin-top-basic">{error}</div>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Cards
            preview={true}
            number={'' + last4}
            name={' '} // {sessionContext.user.firstName + " " + sessionContext.user.lastName}
            expiry={expire}
            cvc={'***'}
            focused={focusedCC}
            issuer={issuer}
          />
        </Grid>
      </Grid>
      <BrowserView className="full-width">
        {isInFlow(location.pathname) && (
          <>
            <br />
            <FlexView
              className={classNames(
                'space-between full-width margin-top-basic',
                {
                  'credit-card-buttons-container': !editMode,
                  'credit-card-buttons-container-edit': editMode,
                }
              )}>
              <BackButton />
              <GreenButton onClick={onEditCreditCard}>
                Begin Coverage
              </GreenButton>
            </FlexView>
            <FlexView
              style={{ justifyContent: 'flex-end' }}
              className={classNames('full-width margin-top-basic')}>
              <div>
                <a
                  target="_blank"
                  href="https://faq.aviation.skywatch.ai/hc/en-us/articles/8839501540754-Can-I-cancel-my-existing-policy-#:~:text=Upon%20binding%20a%20SkyWatch%20Renter's,hours%20of%20the%20renewal%20event"
                  className="margin-right-basic">
                  Cancellation Policy FAQ
                </a>
                <a
                  target="_blank"
                  href="https://skywatchapp-prod.s3.amazonaws.com/common_files/global_sample_policy.pdf">
                  View Sample Policy
                </a>
              </div>
            </FlexView>
            <Divider />
          </>
        )}
      </BrowserView>

      <MobileView>
        {isInFlow(location.pathname) && (
          <BottomSheetPolicySummary
            title={'Begin Coverage'}
            onClick={() => {
              onEditCreditCard()
            }}
          />
        )}
        {!isInFlow(location.pathname) &&
          editMode &&
          (needToRenew ? (
            <>
              <FlexView className="margin-top-basic" hAlignContent={'center'}>
                <GreenButton
                  disabled={!editMode}
                  onClick={onEditCreditCardAndPurache}>
                  Complete purchase
                </GreenButton>
              </FlexView>
              <Grid className="margin-top-basic margin-bottom-medium">
                Your credit card charge has failed and as a result, your renewal
                policy was not created. To finalize your purchase, please update
                your credit card information and click on the "complete
                purchase" button.
              </Grid>
            </>
          ) : (
            <>
              <FlexView className="margin-top-basic" hAlignContent={'center'}>
                <NewFillButton disabled={!editMode} onClick={onEditCreditCard}>
                  Save Changes
                </NewFillButton>
              </FlexView>
            </>
          ))}
        <br /> <br />
        {/* {isInFlow(location.pathname) && (
                    <FlexView>
                        <BackButton />
                        <GreenButton onClick={onEditCreditCard}>
                            Begin Coverage
                        </GreenButton>
                    </FlexView>
                )} */}
        <FlexView
          style={{ justifyContent: 'flex-start' }}
          className={classNames('full-width margin-top-basic')}>
          <div>
            <a
              target="_blank"
              href="https://faq.aviation.skywatch.ai/hc/en-us/articles/8839501540754-Can-I-cancel-my-existing-policy-#:~:text=Upon%20binding%20a%20SkyWatch%20Renter's,hours%20of%20the%20renewal%20event"
              className="margin-right-basic">
              Cancellation Policy FAQ
            </a>
            <a
              target="_blank"
              href="https://skywatchapp-prod.s3.amazonaws.com/common_files/global_sample_policy.pdf">
              View Sample Policy
            </a>
          </div>
        </FlexView>
      </MobileView>

      {/* {isBrowser && <Divider />} */}
      <TermsAndConditionsSection />
    </React.Fragment>
  )
}

export default CreditCard
